export const MASTERDATA_FILE_UPLOAD_URL = `${process.env.BACKEND_URL}master-data/import/fileupload`;

export const DRY_RUN_MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export const MASTER_DATA_PATH = '/master-data';
export const MASTER_DATA_ADD_PATH = '/master-data/add';
export const MASTER_DATA_DETAIL_PATH = '/master-data/:lookupDefinitionId';

// Customer-managed lookup types that can be created and fed with data directly
// (the LookupType union in transformerSchemas.ts is derived from this list)
export const CONCRETE_LOOKUP_TYPES = [
    'ARTICLE_LOOKUP',
    'CLIENT_LOOKUP',
    'CONTACT_LOOKUP',
    'ADDRESS_LOOKUP',
    'CONVERSION_FACTOR_LOOKUP',
    'FRAMEWORK_CONTRACT_LOOKUP',
    'ORDER_MATCHING_LOOKUP',
    'OFFER_MATCHING_LOOKUP',
] as const;

// System-managed mapping lookups (learning data collected via assistance)
export const MAPPING_LOOKUP_TYPES = [
    'CLIENT_MAPPING_LOOKUP',
    'CONTACT_MAPPING_LOOKUP',
    'ARTICLE_MAPPING_LOOKUP',
    'VAT_ID_CHECK_MAPPING_LOOKUP',
    'ADDRESS_MAPPING_LOOKUP',
];

export const ALL_LOOKUP_TYPES = [...CONCRETE_LOOKUP_TYPES, ...MAPPING_LOOKUP_TYPES];

// masterDataConfig fields each document type actually uses, derived from the backend's per-product
// __master_data_config_keys__ (extraction lookups), the products' channel setup utils (mapping
// lookups) and product-specific processing (conversion factor, VAT-ID check). The email lookup is
// resolved generically per inbox, so it applies to every document type.
export const CONFIG_FIELDS_BY_PRODUCT: Record<string, string[]> = {
    rfqs: [
        'clientLookupDefinitionId',
        'contactLookupDefinitionId',
        'clientInternalContactLookupDefinitionId',
        'emailLookupDefinitionId',
        'deliveryAddressLookupDefinitionId',
        'invoiceAddressLookupDefinitionId',
        'frameworkContractLookupDefinitionId',
        'articleLookupDefinitionId',
        'debitorArticleLookupDefinitionId',
        'conversionFactorLookupDefinitionId',
        'clientMappingLookupDefinitionId',
        'contactMappingLookupDefinitionId',
        'articleMappingLookupDefinitionId',
        'deliveryAddressMappingLookupDefinitionId',
    ],
    orders: [
        'clientLookupDefinitionId',
        'contactLookupDefinitionId',
        'clientInternalContactLookupDefinitionId',
        'emailLookupDefinitionId',
        'deliveryAddressLookupDefinitionId',
        'invoiceAddressLookupDefinitionId',
        'frameworkContractLookupDefinitionId',
        'articleLookupDefinitionId',
        'debitorArticleLookupDefinitionId',
        'conversionFactorLookupDefinitionId',
        'offerMatchingLookupDefinitionId',
        'clientMappingLookupDefinitionId',
        'contactMappingLookupDefinitionId',
        'articleMappingLookupDefinitionId',
        'deliveryAddressMappingLookupDefinitionId',
        'invoiceAddressMappingLookupDefinitionId',
    ],
    orderConfirmations: [
        'clientLookupDefinitionId',
        'receiverLookupDefinitionId',
        'emailLookupDefinitionId',
        'deliveryAddressLookupDefinitionId',
        'articleLookupDefinitionId',
        'debitorArticleLookupDefinitionId',
        'orderMatchingLookupDefinitionId',
        'clientMappingLookupDefinitionId',
        'articleMappingLookupDefinitionId',
        'deliveryAddressMappingLookupDefinitionId',
        'invoiceAddressMappingLookupDefinitionId',
    ],
    invoices: [
        'clientLookupDefinitionId',
        'contactLookupDefinitionId',
        'emailLookupDefinitionId',
        'invoiceAddressLookupDefinitionId',
        'articleLookupDefinitionId',
        'debitorArticleLookupDefinitionId',
        'orderMatchingLookupDefinitionId',
        'clientMappingLookupDefinitionId',
        'contactMappingLookupDefinitionId',
        'articleMappingLookupDefinitionId',
        'vatIdCheckMappingLookupDefinitionId',
    ],
    propertyBills: ['clientLookupDefinitionId', 'emailLookupDefinitionId'],
    deliveryNotes: [
        'clientLookupDefinitionId',
        'receiverLookupDefinitionId',
        'emailLookupDefinitionId',
        'deliveryAddressLookupDefinitionId',
        'articleLookupDefinitionId',
        'debitorArticleLookupDefinitionId',
        'conversionFactorLookupDefinitionId',
        'clientMappingLookupDefinitionId',
        'articleMappingLookupDefinitionId',
        'deliveryAddressMappingLookupDefinitionId',
        'invoiceAddressMappingLookupDefinitionId',
    ],
    listOfServices: [
        'clientLookupDefinitionId',
        'contactLookupDefinitionId',
        'emailLookupDefinitionId',
        'deliveryAddressLookupDefinitionId',
        'invoiceAddressLookupDefinitionId',
        'articleLookupDefinitionId',
        'debitorArticleLookupDefinitionId',
        'articleMappingLookupDefinitionId',
    ],
    universal: ['emailLookupDefinitionId'],
};

// Channel masterDataConfig lookup reference fields with the lookup type each may reference,
// grouped into the sections of the channel master data config form (the form and the hub's
// channel assignment UI are both derived from this single list).
export const CONFIG_FORM_SECTIONS: { titleKey: string; fields: { name: string; lookupType: string }[] }[] = [
    {
        titleKey: 'lookupTitle',
        fields: [
            { name: 'clientLookupDefinitionId', lookupType: 'CLIENT_LOOKUP' },
            { name: 'receiverLookupDefinitionId', lookupType: 'CLIENT_LOOKUP' },
            { name: 'contactLookupDefinitionId', lookupType: 'CONTACT_LOOKUP' },
            { name: 'clientInternalContactLookupDefinitionId', lookupType: 'CONTACT_LOOKUP' },
            { name: 'emailLookupDefinitionId', lookupType: 'EMAIL_LOOKUP' },
            { name: 'frameworkContractLookupDefinitionId', lookupType: 'FRAMEWORK_CONTRACT_LOOKUP' },
            { name: 'invoiceAddressLookupDefinitionId', lookupType: 'ADDRESS_LOOKUP' },
            { name: 'deliveryAddressLookupDefinitionId', lookupType: 'ADDRESS_LOOKUP' },
            { name: 'articleLookupDefinitionId', lookupType: 'ARTICLE_LOOKUP' },
            { name: 'debitorArticleLookupDefinitionId', lookupType: 'ARTICLE_LOOKUP' },
            { name: 'conversionFactorLookupDefinitionId', lookupType: 'CONVERSION_FACTOR_LOOKUP' },
        ],
    },
    {
        titleKey: 'mappingTitle',
        fields: [
            { name: 'clientMappingLookupDefinitionId', lookupType: 'CLIENT_MAPPING_LOOKUP' },
            { name: 'contactMappingLookupDefinitionId', lookupType: 'CONTACT_MAPPING_LOOKUP' },
            { name: 'invoiceAddressMappingLookupDefinitionId', lookupType: 'ADDRESS_MAPPING_LOOKUP' },
            { name: 'deliveryAddressMappingLookupDefinitionId', lookupType: 'ADDRESS_MAPPING_LOOKUP' },
            { name: 'articleMappingLookupDefinitionId', lookupType: 'ARTICLE_MAPPING_LOOKUP' },
            { name: 'vatIdCheckMappingLookupDefinitionId', lookupType: 'VAT_ID_CHECK_MAPPING_LOOKUP' },
        ],
    },
    {
        titleKey: 'matchingTitle',
        fields: [
            { name: 'orderMatchingLookupDefinitionId', lookupType: 'ORDER_MATCHING_LOOKUP' },
            { name: 'offerMatchingLookupDefinitionId', lookupType: 'OFFER_MATCHING_LOOKUP' },
        ],
    },
];

export const CONFIG_FORM_FIELDS = CONFIG_FORM_SECTIONS.flatMap((section) => section.fields);

// Channel masterDataConfig fields that can reference a lookup definition of a given type
export const CONFIG_FIELDS_BY_LOOKUP_TYPE: Record<string, string[]> = {};
for (const { name, lookupType } of CONFIG_FORM_FIELDS) {
    (CONFIG_FIELDS_BY_LOOKUP_TYPE[lookupType] ??= []).push(name);
}
